import { useState } from 'react';
import { ArrowLeft, Play, RotateCcw } from 'lucide-react';
import type { Color } from '../../shared/protocol.ts';
import { usePreferences } from '../app/preferences.ts';
import { navigate } from '../app/router.ts';
import { Link } from '../components/Link.tsx';
import { BotAvatar, Strength } from '../components/Brand.tsx';
import { ColorChoice } from '../components/ColorChoice.tsx';
import { BOTS, botByLevel, type BotLevel } from '../lib/bots.ts';
import { readStorage, writeStorage } from '../lib/storage.ts';
import { loadSavedComputerGame, saveComputerGame } from '../hooks/useComputerGame.ts';

const LAST_SETUP_KEY = 'outchess:computer-setup';

/** Two short steps instead of one long page: pick an animal, then pick a colour and go. */
export function ComputerSetupPage() {
  const { t } = usePreferences();
  const last = readStorage<{ level: BotLevel; color: Color | 'random' }>(LAST_SETUP_KEY, { level: 1, color: 'w' });
  const [level, setLevel] = useState<BotLevel>(botByLevel(last.level).level);
  const [color, setColor] = useState<Color | 'random'>(last.color);
  const [step, setStepState] = useState<'pick' | 'ready'>('pick');
  // Steps slide in once the child starts moving between them (not on first arrival).
  const [moved, setMoved] = useState(false);
  const setStep = (next: 'pick' | 'ready') => {
    setStepState(next);
    setMoved(true);
  };
  const stepClass = moved ? 'page-fill step' : 'page-fill';
  const [saved] = useState(() => {
    const game = loadSavedComputerGame();
    return game && !game.result && game.moves.length > 0 ? game : null;
  });

  const bot = botByLevel(level);

  const start = () => {
    writeStorage(LAST_SETUP_KEY, { level, color });
    const resolved: Color = color === 'random' ? (Math.random() < 0.5 ? 'w' : 'b') : color;
    saveComputerGame({ level, color: resolved, moves: [], result: null });
    navigate('/computer/play');
  };

  if (step === 'ready') {
    return (
      <div className={stepClass} key="ready">
        <div className="page-top">
          <button type="button" className="back-link" onClick={() => setStep('pick')}>
            <ArrowLeft size={22} strokeWidth={2.5} /> {t('computer.otherOpponent')}
          </button>
        </div>
        <div className="ready">
          <div className="card ready-card">
            <div className="summary-bot">
              <span className="avatar">
                <BotAvatar bot={bot} />
              </span>
              <h1 className="summary-bot-name">{t(`level.${level}`)}</h1>
              <Strength bot={bot} large />
              <div className="summary-bot-level">{t('computer.levelShort', { level })}</div>
              <p className="summary-bot-desc">{t(`level.${level}.desc`)}</p>
            </div>
            <div className="ready-side">
              <ColorChoice name="color" value={color} onChange={setColor} />
              <button type="button" className="btn btn-green btn-lg btn-block" onClick={start} autoFocus>
                <Play size={26} fill="currentColor" /> {t('computer.start')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={stepClass} key="pick">
      <div className="page-top">
        <Link href="/" className="back-link">
          <ArrowLeft size={22} strokeWidth={2.5} /> {t('nav.home')}
        </Link>
        <h1 className="page-title">{t('computer.level')}</h1>
      </div>

      {saved && (
        <div className="banner" style={{ marginBottom: 'var(--gap)' }}>
          <span>
            <span className="emoji" aria-hidden="true">
              {botByLevel(saved.level).emoji}
            </span>{' '}
            {t('computer.resume')} · {t(`level.${saved.level}`)}
          </span>
          <div className="banner-actions">
            <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate('/computer/play')}>
              <RotateCcw size={18} /> {t('computer.resumeButton')}
            </button>
          </div>
        </div>
      )}

      <div className="level-grid pop-in">
        {BOTS.map((b) => (
          <button
            key={b.level}
            type="button"
            className={`level-card${b.level === last.level ? ' last' : ''}`}
            onClick={() => {
              setLevel(b.level);
              setStep('ready');
            }}
          >
            <span className="avatar">
              <BotAvatar bot={b} />
            </span>
            <span className="level-name">{t(`level.${b.level}`)}</span>
            <Strength bot={b} />
            <span className="visually-hidden">{t('computer.levelShort', { level: b.level })}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
